/**
 * Thin adapter layer over the generated `ConversationApi` client.
 *
 * Provides:
 * - Typed wrappers that unwrap `AxiosResponse.data`
 * - A multipart upload helper for posting messages with optional file attachments
 * - A Blob download helper for attachment streaming
 */

import {ConversationApi} from "@/api/ConversationApi";
import {ApiConfig} from "@/api/http-client";
import type {ChatMessageDTO, ConversationDTO, CreateChatMessageDTO} from "../types";

const apiConfig: ApiConfig = {
    baseURL: "/",
    withXSRFToken: true,
};

const client = new ConversationApi(apiConfig);

/**
 * Fetches the full conversation (all visible chats + timelines) for the given request.
 *
 * Returns `undefined` when the server responds with 204 (no conversation yet, e.g. request
 * is still in DRAFT status) or when the response body is empty. Never returns the empty
 * string — callers should treat `undefined` as "no conversation available".
 */
export async function fetchConversation(requestId: number): Promise<ConversationDTO | undefined> {
    const response = await client.getConversation(requestId);
    // Axios may return an empty string for a 204 (no body) response.
    const raw: unknown = response.data;
    if (response.status === 204 || raw === "" || raw == null) {
        return undefined;
    }
    return raw as ConversationDTO;
}

/**
 * Posts a text message (with optional file attachments) to a chat.
 *
 * The generated client's createFormData serialises object parts as plain strings, which
 * causes Spring's @RequestPart("message") to reject the request with HTTP 415. We bypass
 * the generated client here and post via client.instance directly, constructing the
 * multipart body manually so that the "message" part carries Content-Type: application/json.
 * Axios then sets multipart/form-data with the correct boundary automatically.
 *
 * @param requestId   - the owning request id
 * @param chatId      - the target chat id
 * @param body        - the message text
 * @param attachments - up to 5 files to attach, each ≤ 10 MB; appended under the same
 *                      "attachments" part name so the server receives a repeated part
 */
export async function postMessage(
    requestId: number,
    chatId: number,
    body: string,
    attachments?: File[]
): Promise<ChatMessageDTO> {
    const dto: CreateChatMessageDTO = {body};
    const formData = new FormData();
    formData.append("message", new Blob([JSON.stringify(dto)], {type: "application/json"}));
    for (const file of attachments ?? []) {
        formData.append("attachments", file);
    }
    const response = await client.instance.post<ChatMessageDTO>(
        `/api/requests/${requestId}/conversation/chats/${chatId}/messages`,
        formData
    );
    return response.data;
}

/**
 * Records a read receipt for the current user's company for the given chat.
 * Idempotent — the server ignores subsequent calls.
 */
export async function markChatRead(requestId: number, chatId: number): Promise<void> {
    await client.markRead(requestId, chatId);
}

/**
 * Downloads a message attachment as a Blob.
 * The caller is responsible for creating and revoking the object URL.
 *
 * @returns a Blob containing the raw attachment data
 */
export async function downloadAttachmentBlob(
    requestId: number,
    chatId: number,
    attachmentId: number
): Promise<Blob> {
    // Passing `format: "blob"` sets Axios `responseType: "blob"` so the response body is
    // delivered as a Blob at runtime. The generated client declares `request<string, ...>`,
    // so `response.data` is typed as `string` even though Axios returns a `Blob` when
    // `responseType: "blob"` is active. The double cast `as unknown as Blob` bridges that
    // generated-type / runtime-type mismatch — it is safe as long as the backend sends a
    // binary response and `format: "blob"` is present.
    const response = await client.downloadAttachment(requestId, chatId, attachmentId, {
        format: "blob",
    } as Parameters<typeof client.downloadAttachment>[3]);
    return response.data as unknown as Blob;
}
